/**
 * aC Men's Hub の Meta公式情報アーカイブ API contract（zod schema + 型）。
 * API（apps/api/ac-mens-hub）と Web（apps/web/ac-mens-hub）が共有する。
 *
 * @graph-stack ac-mens-hub
 * @graph-domain Marketing
 * @graph-business Meta公式情報アーカイブの検索・詳細の API ↔ Web 型を共有し、二重定義を排除する。
 * @graph-connects none
 */
import { z } from 'zod';
/**
 * Meta公式情報アーカイブを閲覧する権限。
 *
 * マーケティングチームの複数名が持つ想定で、検索・全文取得・収録状態の参照ができる。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Marketing
 * @graph-business Names the permission required to read the Meta official archive.
 * @graph-connects none
 */
export const KNOWLEDGE_META_PERMISSION = 'knowledge-meta';
/**
 * Meta公式情報アーカイブへ書き込む権限。
 *
 * `KNOWLEDGE_META_PERMISSION` とは**独立した権限**で、閲覧できる相手全員に自動では付かない。
 * このアーカイブは広告運用の判断根拠を引くための一次情報の写しであり、投入・無効化ができる
 * ことは改竄ができることと同義なので、閲覧より狭く配る（付与先は日次同期の実行者だけを想定）。
 *
 * 画面から編集する手段は用意しないが、その前提を守るのは「UI にボタンが無いこと」ではなく
 * この権限分離である。閲覧権限だけを持つ人が API を直接叩いても書き込みは通らない。
 *
 * 権限キーはナビ項目から導出される（`ALL_PAGE_KEYS`）が、`extraPermissions` に載せれば
 * ナビに独立した項目を出さずに権限管理画面から付与できる（`member-staging` と同じ扱い）。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Marketing
 * @graph-business Names the write permission for the Meta official archive, granted independently from the read one.
 * @graph-connects none
 */
export const KNOWLEDGE_META_WRITE_PERMISSION = 'knowledge-meta-write';
/**
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Marketing
 * @graph-business 情報の種類。Meta のどの発信経路から取得したかを表す。ヘルプセンターは運用仕様の正本、エンジニアリングブログは配信アルゴリズムの解説というように信頼度と用途が異なるため、検索結果で必ず区別できるようにする。
 * @graph-connects none
 */
export const KnowledgeMetaSectionSchema = z.enum([
    '01_ヘルプセンター',
    '02_MarketingAPI',
    '03_変更履歴',
    '04_エンジニアリングブログ',
    '05_ニュースルーム',
    '06_その他公式コンテンツ',
    '07_Metaニュースルーム',
]);
/**
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Marketing
 * @graph-business 検索結果1件。本文全体ではなくヒット箇所の抜粋を返す。タイトルだけではどれを読むべきか判断できないため。
 * @graph-connects none
 */
export const KnowledgeMetaEntrySchema = z.object({
    docId: z.string(),
    sourceUrl: z.string(),
    section: KnowledgeMetaSectionSchema,
    title: z.string(),
    snippet: z.string(),
    charCount: z.number(),
    sourceUpdatedAt: z.string().nullable(),
    isActive: z.boolean(),
});
/**
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Marketing
 * @graph-business 種類ごとの該当件数。全体の収録数ではなく「その検索でいくつ当たったか」を返し、ワンクリックで絞り込めるようにする。
 * @graph-connects none
 */
export const KnowledgeMetaSectionCountSchema = z.object({
    section: KnowledgeMetaSectionSchema,
    count: z.number(),
});
/**
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Marketing
 * @graph-business 検索の入力。キーワードは必須。空文字での全件取得は 6,090万字を返しうるため許可しない。
 * @graph-connects none
 */
export const KnowledgeMetaSearchQuerySchema = z.object({
    keyword: z.string().min(1).max(100),
    section: KnowledgeMetaSectionSchema.optional(),
    limit: z.number().int().min(1).max(100).default(30),
});
/**
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Marketing
 * @graph-business 検索の応答。総ヒット数と種類別内訳を添えて、絞り込みの判断材料にする。
 * @graph-connects none
 */
export const KnowledgeMetaSearchResponseSchema = z.object({
    total: z.number(),
    entries: z.array(KnowledgeMetaEntrySchema),
    sectionCounts: z.array(KnowledgeMetaSectionCountSchema),
});
/**
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Marketing
 * @graph-business 詳細取得の入力。
 * @graph-connects none
 */
export const KnowledgeMetaDetailQuerySchema = z.object({
    docId: z.string().min(1),
});
/**
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Marketing
 * @graph-business 記事の全文。出典URLを必ず伴う。出典を辿れない情報は一次情報として機能しないため。
 * @graph-connects none
 */
export const KnowledgeMetaDetailSchema = KnowledgeMetaEntrySchema.omit({ snippet: true }).extend({
    body: z.string(),
});
// ============================================================
// 取り込み（母艦の日次同期 → Hub API → BigQuery）
// ============================================================
/**
 * 本文の受け入れ上限（文字数）。
 *
 * 収録済み 12,184 件の実測最大は 106,170 字・p99 は 23,166 字（2026-09-08）。
 * 3 倍弱の余裕を持たせつつ、1 リクエストの最大body量を見積もれる値に固定する。
 * 超える記事が現れたら取り込みが失敗して気づけるほうが、黙って切り詰めるより安全。
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Marketing
 * @graph-business Meta公式情報の本文受け入れ上限。実測最大の3倍弱に置き、超過は黙って切り詰めず失敗させる。
 * @graph-connects none
 */
export const KNOWLEDGE_META_BODY_MAX_CHARS = 300_000;
/**
 * 1 リクエストで送れる記事数の上限。
 *
 * 本文上限 300,000 字は日本語 UTF-8 で最大 900KB になる。20 件で最悪 18MB となり
 * Cloud Run のリクエスト上限（32MB）の内側に収まる。日次の差分は 70 件前後なので
 * 4 リクエストで足りる。
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Marketing
 * @graph-business 1リクエストあたりの記事数上限。本文上限と掛けてもCloud Runのリクエスト上限に収まる値。
 * @graph-connects none
 */
export const KNOWLEDGE_META_INGEST_BATCH_MAX = 20;
/**
 * 一度に無効化できる記事数の上限。
 *
 * 無効化は「Meta から消えた記事を検索結果から外す」操作で、母艦側の取得が
 * 一時的に失敗しただけでも大量に発生しうる。上限を置いて、取りこぼしが
 * そのまま全件無効化に化けないようにする。
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Marketing
 * @graph-business 一度に無効化できる記事数の上限。母艦側の取得失敗が全件無効化へ波及しないための歯止め。
 * @graph-connects none
 */
export const KNOWLEDGE_META_DEACTIVATE_MAX = 1000;
/** doc_id / content_hash は SHA-256 の16進表記。 @graph-connects none */
const Sha256HexSchema = z.string().regex(/^[0-9a-f]{64}$/);
/**
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Marketing
 * @graph-business 取り込む記事1件。文字数と同期時刻はサーバ側で決めるため受け取らない。本文と食い違う文字数が入り込む経路を作らないため。
 * @graph-connects none
 */
export const KnowledgeMetaDocumentSchema = z.object({
    docId: Sha256HexSchema,
    sourceUrl: z.url().max(1000),
    section: KnowledgeMetaSectionSchema,
    title: z.string().min(1).max(500),
    body: z.string().min(1).max(KNOWLEDGE_META_BODY_MAX_CHARS),
    contentHash: Sha256HexSchema,
    sourceUpdatedAt: z.iso.datetime().nullable(),
    fetchedAt: z.iso.datetime(),
});
/**
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Marketing
 * @graph-business 取り込みの入力。1リクエストの件数を制限し、母艦側で分割して送らせる。
 * @graph-connects none
 */
export const KnowledgeMetaIngestInputSchema = z.object({
    documents: z.array(KnowledgeMetaDocumentSchema).min(1).max(KNOWLEDGE_META_INGEST_BATCH_MAX),
});
/**
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Marketing
 * @graph-business 取り込みの結果。書き込んだ件数を返し、母艦側のログと突合できるようにする。
 * @graph-connects none
 */
export const KnowledgeMetaIngestResultSchema = z.object({
    upserted: z.number(),
});
/**
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Marketing
 * @graph-business 収録済み記事の識別子と内容ハッシュ。母艦がこれと手元を突き合わせ、変わった記事だけを送るために使う。
 * @graph-connects none
 */
export const KnowledgeMetaStateEntrySchema = z.object({
    docId: z.string(),
    contentHash: z.string(),
    isActive: z.boolean(),
});
/**
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Marketing
 * @graph-business 収録済み全件の状態。本文を含まないため 12,000件でも数MBに収まる。
 * @graph-connects none
 */
export const KnowledgeMetaStateResponseSchema = z.object({
    entries: z.array(KnowledgeMetaStateEntrySchema),
});
/**
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Marketing
 * @graph-business 無効化の入力。物理削除はしない。過去にどの記述を根拠に判断したかを後から辿れなくなるため。
 * @graph-connects none
 */
export const KnowledgeMetaDeactivateInputSchema = z.object({
    docIds: z.array(Sha256HexSchema).min(1).max(KNOWLEDGE_META_DEACTIVATE_MAX),
});
/**
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Marketing
 * @graph-business 無効化の結果。実際に状態が変わった件数を返す。
 * @graph-connects none
 */
export const KnowledgeMetaDeactivateResultSchema = z.object({
    deactivated: z.number(),
});
//# sourceMappingURL=knowledge-meta.js.map